import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PostService } from '../../services/post.service';
import { UserSessionService } from '../../services/user-session.service';
import { DocumentService } from '../../services/document.service';
import { UserService } from '../../services/user.service';
import { ControlQuestionService, ControlQuestionSurvey } from '../../services/controlquestion.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { API_BASE_URL } from '../../services/api-base';
import { toLocalISODate, toLocalISOString } from '../../utils/date-utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-intranet-dash',
  templateUrl: './intranet-dash.component.html',
  styleUrl: './intranet-dash.component.scss'
})
export class IntranetDashComponent implements OnInit, OnDestroy {
  readonly maxDocumentFileSizeMb = 30;
  readonly maxDocumentFileSizeBytes = this.maxDocumentFileSizeMb * 1024 * 1024;
  activeMainTab: 'dashboard' | 'posts' | 'documents' | 'questions' = 'dashboard';
  posts: any[] = [];
  filteredPosts: any[] = [];
  pagedPosts: any[] = [];
  loadingPosts = false;
  postsError = '';
  metrics: Array<{ title: string; value: number; description: string; icon: string; accent: string }> = [];
  dashboardPeriodMonths = 3;
  dashboardPeriodOptions = [3, 6, 12];
  intranetDashboardKpis = {
    postsPeriod: 0,
    likesPeriod: 0,
    commentsPeriod: 0,
    interactionsPeriod: 0,
    avgInteractionsPerPost: 0
  };
  intranetDailySeries: Array<{ label: string; likes: number; comments: number; posts: number; views: number }> = [];
  intranetDailyMax = 1;
  intranetTypeRows: Array<{ label: string; count: number; likes: number; comments: number; percent: number; color: string }> = [];
  intranetTypeMax = 1;
  intranetTopPosts: Array<{ title: string; author: string; score: number; likes: number; comments: number; dateLabel: string }> = [];
  intranetInsights = {
    avgLikesPerDay: 0,
    avgCommentsPerDay: 0,
    avgPostsPerDay: 0,
    totalViewsPeriod: 0
  };
  intranetLineTooltip = {
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: ''
  };
  pendingDocuments: any[] = [];
  loadingPendingDocs = false;
  pendingDocsError = '';
  filters = {
    date: '',
    status: '',
    author: ''
  };
  statusOptions = [
    { value: '', labelKey: 'INTRANET_DASH.STATUS.ALL' },
    { value: 'published', labelKey: 'INTRANET_DASH.STATUS.PUBLISHED' },
    { value: 'scheduled', labelKey: 'INTRANET_DASH.STATUS.SCHEDULED' },
    { value: 'draft', labelKey: 'INTRANET_DASH.STATUS.DRAFT' },
    { value: 'hidden', labelKey: 'INTRANET_DASH.STATUS.HIDDEN' }
  ];
  currentPage = 1;
  totalPages = 1;
  pageSize = 6;
  actionMenuOpen: number | null = null;
  currentUser: any = null;
  private userSub?: Subscription;
  private langSub?: Subscription;
  private readonly apiBase = `${API_BASE_URL}`;
  documentDispatchOpen = false;
  documentSource: 'existing' | 'upload' = 'existing';
  existingDocuments: any[] = [];
  documentSearch = '';
  selectedDocumentId: number | null = null;
  newDocumentForm = {
    title: '',
    description: '',
    document_type: 'POLICY',
    is_public_document: false,
    require_read: true,
    require_accept: false,
    require_agreement: false,
    require_signature: false,
    require_selfie: false,
    require_geolocation: false
  };
  documentTypeOptions = [
    { value: 'POLICY', labelKey: 'INTRANET_DASH.DOC_TYPE.POLICY' },
    { value: 'CONTRACT', labelKey: 'INTRANET_DASH.DOC_TYPE.CONTRACT' },
    { value: 'TERM', labelKey: 'INTRANET_DASH.DOC_TYPE.TERM' },
    { value: 'NOTICE', labelKey: 'INTRANET_DASH.DOC_TYPE.NOTICE' },
    { value: 'OTHER', labelKey: 'INTRANET_DASH.DOC_TYPE.OTHER' }
  ];
  documentFile: File | null = null;
  documentFileName = '';
  documentFileError = '';
  dispatchAudience: 'ALL_USERS' | 'SPECIFIC_USERS' = 'ALL_USERS';
  tenantUsers: any[] = [];
  userSearch = '';
  selectedUserIds = new Set<number>();
  loadingDocuments = false;
  loadingRecipients = false;
  dispatchFeedback = '';
  savingDispatch = false;
  questionDispatchOpen = false;
  intranetSurveys: ControlQuestionSurvey[] = [];
  surveySearch = '';
  selectedSurveyId: number | null = null;
  loadingSurveys = false;
  questionDispatchFeedback = '';
  savingQuestionDispatch = false;
  questionAudience: 'ALL_USERS' | 'SECTOR' | 'DEPARTMENT' | 'LOCATION' | 'SPECIFIC_USERS' = 'ALL_USERS';
  questionSectors: any[] = [];
  questionDepartments: any[] = [];
  questionLocations: any[] = [];
  selectedSectorId: number | null = null;
  selectedDepartmentId: number | null = null;
  selectedLocationId: number | null = null;
  questionUserSearch = '';
  selectedQuestionUserIds = new Set<number>();
  questionDispatches: any[] = [];
  questionDispatchesLoading = false;
  questionDispatchesError = '';
  questionDispatchesPage = 1;
  questionDispatchesPageSize = 8;
  questionDispatchesTotal = 0;

  constructor(
    private postService: PostService,
    private userSession: UserSessionService,
    private router: Router,
    private documentService: DocumentService,
    private userService: UserService,
    private controlquestion: ControlQuestionService,
    private orgStructure: OrgStructureService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.userSub = this.userSession.user$.subscribe(user => {
      this.currentUser = user || this.userSession.getUser(true);
      if (this.currentUser) {
        if (!this.hasDashboardAccess()) {
          this.router.navigate(['/feed']);
          return;
        }
        this.loadPosts();
        this.loadPendingDocuments();
      }
    });
    this.langSub = this.translate.onLangChange.subscribe(() => this.updateMetrics());
    if (!this.currentUser) {
      this.currentUser = this.userSession.getUser();
      if (this.currentUser) {
        if (!this.hasDashboardAccess()) {
          this.router.navigate(['/feed']);
          return;
        }
        this.loadPosts();
        this.loadPendingDocuments();
      }
    }
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
    this.langSub?.unsubscribe();
  }

  setActiveMainTab(tab: 'dashboard' | 'posts' | 'documents' | 'questions') {
    this.activeMainTab = tab;
    if (tab === 'questions') {
      this.loadQuestionDispatches(1);
    }
  }

  setDashboardPeriod(months: number) {
    if (this.dashboardPeriodMonths === months) {
      return;
    }
    this.dashboardPeriodMonths = months;
    this.buildIntranetDashboardInsights();
  }

  openNewPost() {
    this.router.navigate(['/intranet-post']);
  }

  private hasDashboardAccess(): boolean {
    const modules = this.currentUser?.modules;
    const roleName = String(this.currentUser?.role_name || '').toLowerCase();
    const roleId = Number(this.currentUser?.role_id || 0);
    const isMaster = !!this.currentUser?.is_master || roleId === 1 || roleName.includes('master') || roleName.includes('admin');
    if (isMaster) {
      return true;
    }
    if (!Array.isArray(modules)) {
      return true;
    }
    const intranet = modules.find((mod: any) => mod.module_key === 'intranet');
    if (!intranet) {
      return false;
    }
    return !!(intranet.can_create || intranet.can_manage);
  }

  loadPosts() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.loadingPosts = true;
    this.postsError = '';
    this.postService.getDashboardPosts(this.currentUser.tenant_id).subscribe({
      next: (posts) => {
        this.posts = posts.map(post => ({
          ...post,
          allow_comments: this.parseFlag(post.allow_comments, true),
          allow_likes: this.parseFlag(post.allow_likes, true)
        }));
        this.loadingPosts = false;
        this.updateMetrics();
        this.applyFilters();
      },
      error: (err) => {
        this.postsError = err?.error?.error || 'Não foi possível carregar os posts.';
        this.loadingPosts = false;
      }
    });
  }

  editPost(post: any) {
    const target = post.post_type === 'document' ? '/control-sign' : '/intranet-post';
    this.router.navigate([target], { queryParams: { postId: post.id } });
  }

  toggleVisibility(post: any) {
    if (!this.currentUser?.id) {
      return;
    }
    const nextStatus = post.status === 'hidden' ? 'published' : 'hidden';
    this.postService.updatePost(post.id, { status: nextStatus, updated_by: this.currentUser.id }).subscribe({
      next: () => {
        post.status = nextStatus;
        this.applyFilters();
        this.updateMetrics();
      },
      error: () => {}
    });
  }

  toggleComments(post: any, checked: boolean) {
    if (!this.currentUser?.id) {
      return;
    }
    post.allow_comments = checked;
    this.postService.updatePost(post.id, { allow_comments: checked, updated_by: this.currentUser.id }).subscribe({
      error: () => {
        post.allow_comments = !checked;
      }
    });
  }

  togglePopupHighlight(post: any, enabled: boolean) {
    if (!this.currentUser?.id) {
      return;
    }
    const payload: any = {
      show_popup: enabled,
      updated_by: this.currentUser.id,
      popup_expires_at: enabled
        ? (post.popup_expires_at || toLocalISOString(new Date(Date.now() + 24 * 60 * 60 * 1000)))
        : null
    };
    if (enabled) {
      payload.allow_comments = false;
      payload.allow_likes = false;
      payload.status = 'published';
    } else {
      payload.status = 'hidden';
    }
    this.postService.updatePost(post.id, payload).subscribe({
      next: () => {
        post.show_popup = enabled;
        post.popup_expires_at = payload.popup_expires_at;
        if (enabled) {
          post.allow_comments = false;
          post.allow_likes = false;
          post.status = 'published';
        }
        if (!enabled) {
          post.status = 'hidden';
        }
      },
      error: () => {}
    });
  }

  private parseFlag(value: any, fallback: boolean) {
    if (value === undefined || value === null) {
      return fallback;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    const normalized = Number(value);
    if (!Number.isNaN(normalized)) {
      return normalized === 1;
    }
    return Boolean(value);
  }

  statusLabel(status: string | null | undefined) {
    const map: Record<string, string> = {
      published: 'Publicado',
      draft: 'Rascunho',
      scheduled: 'Agendado',
      hidden: 'Oculto'
    };
    if (!status) {
      return '—';
    }
    return map[status] || status;
  }

  postExcerpt(post: any) {
    const text = post?.content_text;
    if (!text) {
      return '';
    }
    const limit = 50;
    return text.length > limit ? `${text.slice(0, limit - 3)}...` : text;
  }

  thumbnailUrl(post: any) {
    if (this.isVideo(post)) {
      return null;
    }
    const media = (post?.media_urls && post.media_urls[0]) || post?.file_url;
    if (!media) {
      return null;
    }
    const path = String(media);
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.apiBase}${path}`;
  }

  formatDateLabel(value: any) {
    if (!value) {
      return '—';
    }
    return new Date(value).toLocaleString();
  }

  isVideo(post: any) {
    return String(post?.post_type).toLowerCase() === 'video';
  }

  applyFilters() {
    const filtered = this.posts.filter((post) => {
      let ok = true;
      if (this.filters.date) {
        ok = ok && this.isSameDate(post.posted_at, this.filters.date);
      }
      if (this.filters.status) {
        ok = ok && String(post.status) === this.filters.status;
      }
      if (this.filters.author) {
        const target = (post.author_name || '').toLowerCase();
        ok = ok && target.includes(this.filters.author.toLowerCase());
      }
      return ok;
    });
    this.filteredPosts = filtered;
    this.currentPage = 1;
    this.updatePagination();
  }

  private isSameDate(value: any, dateFilter: string) {
    if (!value) {
      return false;
    }
    const dateValue = new Date(value);
    return toLocalISODate(dateValue) === dateFilter;
  }

  private updatePagination() {
    this.totalPages = Math.max(1, Math.ceil(this.filteredPosts.length / this.pageSize));
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedPosts = this.filteredPosts.slice(start, start + this.pageSize);
  }

  changePage(step: number) {
    const nextPage = this.currentPage + step;
    if (nextPage < 1 || nextPage > this.totalPages) {
      return;
    }
    this.currentPage = nextPage;
    this.updatePagination();
  }

  onActionMenuToggle(event: MouseEvent, postId: number) {
    event.stopPropagation();
    this.actionMenuOpen = this.actionMenuOpen === postId ? null : postId;
  }

  @HostListener('document:click')
  closeActionMenu() {
    this.actionMenuOpen = null;
  }

  onFiltersChanged() {
    this.applyFilters();
  }

  canEdit(post: any) {
    return ['draft', 'scheduled'].includes(String(post?.status));
  }

  private updateMetrics() {
    const published = this.posts.filter(post => post.status === 'published').length;
    const scheduled = this.posts.filter(post => post.status === 'scheduled').length;
    const totalLikes = this.posts.reduce((acc, post) => acc + (Number(post.likes_count) || 0), 0);
    const totalComments = this.posts.reduce((acc, post) => acc + (Number(post.comments_count) || 0), 0);

    this.metrics = [
      {
        title: this.translate.instant('INTRANET_DASH.METRICS.PUBLISHED'),
        value: published,
        description: this.translate.instant('INTRANET_DASH.METRICS.PUBLISHED_DESC'),
        icon: 'campaign',
        accent: 'primary'
      },
      {
        title: this.translate.instant('INTRANET_DASH.METRICS.SCHEDULED'),
        value: scheduled,
        description: this.translate.instant('INTRANET_DASH.METRICS.SCHEDULED_DESC'),
        icon: 'event',
        accent: 'secondary'
      },
      {
        title: this.translate.instant('INTRANET_DASH.METRICS.LIKES'),
        value: totalLikes,
        description: this.translate.instant('INTRANET_DASH.METRICS.LIKES_DESC'),
        icon: 'favorite',
        accent: 'tertiary'
      },
      {
        title: this.translate.instant('INTRANET_DASH.METRICS.COMMENTS'),
        value: totalComments,
        description: this.translate.instant('INTRANET_DASH.METRICS.COMMENTS_DESC'),
        icon: 'chat_bubble',
        accent: 'quaternary'
      }
    ];
    this.buildIntranetDashboardInsights();
  }

  private buildIntranetDashboardInsights() {
    const months = this.dashboardPeriodMonths;
    const now = new Date();
    const endMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startMonth = new Date(endMonth.getFullYear(), endMonth.getMonth() - (months - 1), 1);
    const endDate = new Date(endMonth.getFullYear(), endMonth.getMonth() + 1, 0, 23, 59, 59, 999);

    const periodMap = new Map<string, { label: string; likes: number; comments: number; posts: number; views: number }>();
    for (let offset = 0; offset < months; offset++) {
      const monthDate = new Date(startMonth.getFullYear(), startMonth.getMonth() + offset, 1);
      const key = this.getMonthKey(monthDate);
      periodMap.set(key, {
        label: this.getMonthLabel(monthDate),
        likes: 0,
        comments: 0,
        posts: 0,
        views: 0
      });
    }

    const postsInPeriod = this.posts.filter((post) => {
      const date = this.resolvePostDate(post);
      if (!date) {
        return false;
      }
      return date >= startMonth && date <= endDate;
    });

    const typeMap = new Map<string, { label: string; count: number; likes: number; comments: number }>();
    postsInPeriod.forEach((post) => {
      const likes = Number(post.likes_count) || 0;
      const comments = Number(post.comments_count) || 0;
      const views = Number(post.views) || 0;
      const date = this.resolvePostDate(post);
      if (!date) {
        return;
      }
      const key = this.getMonthKey(date);
      const monthRow = periodMap.get(key);
      if (monthRow) {
        monthRow.likes += likes;
        monthRow.comments += comments;
        monthRow.posts += 1;
        monthRow.views += views;
      }

      const rawType = String(post.post_type || 'outro').trim();
      const typeKey = rawType.toLowerCase();
      const typeLabel = rawType.charAt(0).toUpperCase() + rawType.slice(1);
      const current = typeMap.get(typeKey) || { label: typeLabel, count: 0, likes: 0, comments: 0 };
      current.count += 1;
      current.likes += likes;
      current.comments += comments;
      typeMap.set(typeKey, current);
    });

    const periodSeries = Array.from(periodMap.values());
    const totalLikes = periodSeries.reduce((sum, row) => sum + row.likes, 0);
    const totalComments = periodSeries.reduce((sum, row) => sum + row.comments, 0);
    const totalPosts = periodSeries.reduce((sum, row) => sum + row.posts, 0);
    const totalViews = periodSeries.reduce((sum, row) => sum + row.views, 0);
    const interactions = totalLikes + totalComments;

    this.intranetDashboardKpis = {
      postsPeriod: totalPosts,
      likesPeriod: totalLikes,
      commentsPeriod: totalComments,
      interactionsPeriod: interactions,
      avgInteractionsPerPost: totalPosts ? interactions / totalPosts : 0
    };

    this.intranetDailySeries = periodSeries;
    this.intranetDailyMax = Math.max(1, ...periodSeries.map(row => row.likes + row.comments));

    const typePalette = ['#22b8b3', '#4fb7e8', '#7b8cff', '#f0cb59', '#8fa2bd', '#45d0b6'];
    this.intranetTypeRows = Array.from(typeMap.values())
      .sort((a, b) => b.count - a.count)
      .map((row, index) => ({
        ...row,
        percent: totalPosts ? Math.round((row.count / totalPosts) * 100) : 0,
        color: typePalette[index % typePalette.length]
      }));
    this.intranetTypeMax = Math.max(1, ...this.intranetTypeRows.map(row => row.count));

    this.intranetTopPosts = postsInPeriod
      .map((post) => {
        const likes = Number(post.likes_count) || 0;
        const comments = Number(post.comments_count) || 0;
        const score = likes + comments;
        return {
          title: this.resolvePostTitle(post),
          author: post.author_name || 'Usuário',
          score,
          likes,
          comments,
          dateLabel: this.formatDateLabel(this.resolvePostDate(post))
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);

    this.intranetInsights = {
      avgLikesPerDay: totalLikes / months,
      avgCommentsPerDay: totalComments / months,
      avgPostsPerDay: totalPosts / months,
      totalViewsPeriod: totalViews
    };
  }

  private getMonthKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private getMonthLabel(date: Date) {
    const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);
    const yearLabel = String(date.getFullYear()).slice(-2);
    return `${monthLabel} ${yearLabel}`;
  }

  intranetBarPercent(value: number, max: number) {
    if (!max) {
      return 0;
    }
    return Math.max(0, Math.min(100, (value / max) * 100));
  }

  get intranetInteractionSeriesValues() {
    return this.intranetDailySeries.map((row) => Number(row.likes || 0) + Number(row.comments || 0));
  }

  get intranetInteractionTrendSeriesValues() {
    const values = this.intranetInteractionSeriesValues;
    if (!values.length) {
      return [];
    }
    return values.map((_, index) => {
      const current = values[index] || 0;
      const prev = values[index - 1] || current;
      const next = values[index + 1] || current;
      return Number(((prev + current + next) / 3).toFixed(2));
    });
  }

  get intranetInteractionsAveragePerDay() {
    const values = this.intranetInteractionSeriesValues;
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(2));
  }

  get intranetInteractionsAveragePerDayLabel() {
    return this.intranetInteractionsAveragePerDay.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  get intranetLineMaxValue() {
    return Math.max(...this.intranetInteractionSeriesValues, ...this.intranetInteractionTrendSeriesValues, 1);
  }

  intranetLinePointList(values: number[]) {
    return this.intranetLineDots(values)
      .map((point) => `${point.x},${point.y}`)
      .join(' ');
  }

  intranetLineYByValue(value: number) {
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.intranetLineMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  get intranetLineYAxisTicks() {
    const max = Math.max(1, Math.ceil(this.intranetLineMaxValue));
    const divisions = 4;
    const step = max / divisions;
    const ticks: Array<{ label: string; value: number; y: number }> = [];
    for (let i = 0; i <= divisions; i += 1) {
      const value = Number((max - i * step).toFixed(1));
      const pretty = Number.isInteger(value) ? `${value}` : value.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      ticks.push({
        label: pretty,
        value,
        y: this.intranetLineYByValue(value)
      });
    }
    return ticks;
  }

  intranetLineDots(values: number[]) {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 616;
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.intranetLineMaxValue;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((value, index) => {
      const x = widthStart + step * index;
      const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
      points.push({
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
        value: Number(value || 0),
        label: this.intranetDailySeries[index]?.label || ''
      });
    });
    return points;
  }

  intranetLinePointEnter(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'interactions' | 'trend'
  ) {
    this.updateIntranetLineTooltip(event, point, series);
  }

  intranetLinePointMove(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'interactions' | 'trend'
  ) {
    this.updateIntranetLineTooltip(event, point, series);
  }

  intranetLinePointLeave() {
    this.intranetLineTooltip.visible = false;
  }

  intranetNumber(value: number) {
    return Number.isFinite(value) ? value.toLocaleString('pt-BR') : '0';
  }

  intranetDecimal(value: number) {
    return Number.isFinite(value)
      ? value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      : '0,0';
  }

  get intranetTypeDonutStyle() {
    if (!this.intranetTypeRows.length) {
      return 'conic-gradient(#e5edf7 0 100%)';
    }
    let cursor = 0;
    const parts = this.intranetTypeRows.map((row) => {
      const slice = row.percent > 0 ? row.percent : 0;
      const start = cursor;
      cursor += slice;
      return `${row.color} ${start}% ${cursor}%`;
    });
    if (cursor < 100) {
      parts.push(`#e5edf7 ${cursor}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  private resolvePostTitle(post: any) {
    const text = String(post?.content_text || '').trim();
    if (text) {
      const summary = text.replace(/\s+/g, ' ');
      return summary.length > 78 ? `${summary.slice(0, 75)}...` : summary;
    }
    return `Postagem #${post?.id || '—'}`;
  }

  private resolvePostDate(post: any): Date | null {
    const value = post?.posted_at || post?.scheduled_at || post?.created_at;
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private updateIntranetLineTooltip(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'interactions' | 'trend'
  ) {
    const target = event.currentTarget as SVGCircleElement | null;
    const container = target?.closest('.intranet-v2-line-chart') as HTMLElement | null;
    if (!container) {
      this.intranetLineTooltip.visible = false;
      return;
    }
    const rect = container.getBoundingClientRect();
    let x = event.clientX - rect.left + 14;
    let y = event.clientY - rect.top - 12;
    x = Math.max(90, Math.min(x, rect.width - 20));
    y = Math.max(26, Math.min(y, rect.height - 20));
    const isInteractions = series === 'interactions';
    this.intranetLineTooltip = {
      visible: true,
      x,
      y,
      title: `${isInteractions ? 'Interações' : 'Tendência'} · ${point.label}`,
      value: isInteractions
        ? `${point.value} (likes + comentários)`
        : `${point.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} (média móvel)`
    };
  }

  private loadPendingDocuments() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id) {
      this.pendingDocuments = [];
      return;
    }
    this.loadingPendingDocs = true;
    this.pendingDocsError = '';
    this.documentService.getPendingDocuments(this.currentUser.id, this.currentUser.tenant_id).subscribe({
      next: (docs) => {
        this.pendingDocuments = Array.isArray(docs) ? docs : [];
        this.loadingPendingDocs = false;
      },
      error: () => {
        this.pendingDocsError = 'Não foi possível carregar os documentos pendentes.';
        this.pendingDocuments = [];
        this.loadingPendingDocs = false;
      }
    });
  }

  openPendingDocuments() {
    this.router.navigate(['/feed']);
  }

  openDispatchModal() {
    if (!this.currentUser) {
      return;
    }
    this.resetDispatchState();
    this.documentDispatchOpen = true;
    this.loadDocuments();
    this.loadTenantUsers();
  }

  closeDispatchModal() {
    this.documentDispatchOpen = false;
    this.dispatchFeedback = '';
    this.documentFileError = '';
  }

  onDocumentSourceChange(mode: 'existing' | 'upload') {
    this.documentSource = mode;
    this.dispatchFeedback = '';
    if (mode === 'existing' && this.existingDocuments.length) {
      this.selectedDocumentId = this.existingDocuments[0].id;
    } else if (mode === 'existing') {
      this.selectedDocumentId = null;
    }
  }

  onPublicDocumentToggle() {
    if (!this.newDocumentForm.is_public_document) {
      return;
    }
    this.newDocumentForm.require_read = false;
    this.newDocumentForm.require_accept = false;
    this.newDocumentForm.require_agreement = false;
    this.newDocumentForm.require_signature = false;
    this.newDocumentForm.require_selfie = false;
    this.newDocumentForm.require_geolocation = false;
    this.dispatchAudience = 'ALL_USERS';
    this.selectedUserIds.clear();
  }

  onDocumentFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const nextFile = input.files[0];
      this.documentFileError = '';
      if (!this.isDocumentFileSizeAllowed(nextFile)) {
        this.documentFile = null;
        this.documentFileName = '';
        this.dispatchFeedback = this.documentFileError;
        input.value = '';
        return;
      }
      this.documentFile = nextFile;
      this.documentFileName = this.documentFile.name;
      this.dispatchFeedback = '';
      input.value = '';
    }
  }

  private isDocumentFileSizeAllowed(file: File) {
    if (file.size <= this.maxDocumentFileSizeBytes) {
      return true;
    }
    this.documentFileError = `Arquivo excede o limite de ${this.maxDocumentFileSizeMb}MB.`;
    return false;
  }

  toggleRecipientMode(mode: 'ALL_USERS' | 'SPECIFIC_USERS') {
    this.dispatchAudience = mode;
    if (mode === 'ALL_USERS') {
      this.selectedUserIds.clear();
    }
  }

  toggleRecipientSelection(userId: number) {
    if (this.selectedUserIds.has(userId)) {
      this.selectedUserIds.delete(userId);
    } else {
      this.selectedUserIds.add(userId);
    }
  }

  get filteredDocuments() {
    if (!this.documentSearch) {
      return this.existingDocuments;
    }
    const term = this.documentSearch.toLowerCase();
    return this.existingDocuments.filter(doc => doc.title?.toLowerCase().includes(term));
  }

  get filteredTenantUsers() {
    if (!this.userSearch) {
      return this.tenantUsers;
    }
    const term = this.userSearch.toLowerCase();
    return this.tenantUsers.filter(user =>
      user.name?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term)
    );
  }

  dispatchDocument() {
    if (!this.currentUser) {
      return;
    }
    this.dispatchFeedback = '';
    if (this.documentSource === 'existing') {
      const docId = Number(this.selectedDocumentId);
      if (!docId) {
        this.dispatchFeedback = 'Selecione um documento existente.';
        return;
      }
      if (this.newDocumentForm.is_public_document) {
        this.publishExistingDocumentAsPublic(docId);
        return;
      }
      this.executeDispatch(docId);
      return;
    }

    if (!this.documentFile || !this.newDocumentForm.title) {
      this.dispatchFeedback = 'Informe o título e selecione um arquivo PDF.';
      return;
    }
    if (!this.isDocumentFileSizeAllowed(this.documentFile)) {
      this.dispatchFeedback = this.documentFileError;
      return;
    }

    if (this.newDocumentForm.is_public_document) {
      this.createPublicDocument();
      return;
    }

    const formData = new FormData();
    formData.append('tenant_id', this.currentUser.tenant_id);
    formData.append('title', this.newDocumentForm.title);
    formData.append('description', this.newDocumentForm.description || '');
    formData.append('document_type', this.newDocumentForm.document_type);
    formData.append('require_read', this.newDocumentForm.require_read ? '1' : '0');
    formData.append('require_accept', this.newDocumentForm.require_accept ? '1' : '0');
    formData.append('require_agreement', this.newDocumentForm.require_agreement ? '1' : '0');
    formData.append('require_signature', this.newDocumentForm.require_signature ? '1' : '0');
    formData.append('require_selfie', this.newDocumentForm.require_selfie ? '1' : '0');
    formData.append('require_geolocation', this.newDocumentForm.require_geolocation ? '1' : '0');
    formData.append('created_by', this.currentUser.id);
    formData.append('file', this.documentFile);

    this.savingDispatch = true;
    this.documentService.createDocument(formData).subscribe({
      next: (doc) => {
        this.existingDocuments.unshift(doc);
        this.selectedDocumentId = doc.id;
        this.executeDispatch(doc.id);
      },
      error: (err) => {
        this.savingDispatch = false;
        this.dispatchFeedback = err?.error?.error || 'Erro ao salvar o documento.';
      }
    });
  }

  private createPublicDocument() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id || !this.documentFile) {
      this.dispatchFeedback = 'Informe o documento que será publicado.';
      return;
    }
    if (!this.isDocumentFileSizeAllowed(this.documentFile)) {
      this.dispatchFeedback = this.documentFileError;
      return;
    }

    const formData = new FormData();
    formData.append('tenant_id', this.currentUser.tenant_id);
    formData.append('title', this.newDocumentForm.title);
    formData.append('description', this.newDocumentForm.description || '');
    formData.append('document_type', this.newDocumentForm.document_type);
    formData.append('require_read', '0');
    formData.append('require_accept', '0');
    formData.append('require_agreement', '0');
    formData.append('require_signature', '0');
    formData.append('require_selfie', '0');
    formData.append('require_geolocation', '0');
    formData.append('is_public_document', '1');
    formData.append('created_by', this.currentUser.id);
    formData.append('file', this.documentFile);

    this.savingDispatch = true;
    this.documentService.createDocument(formData).subscribe({
      next: () => {
        this.savingDispatch = false;
        this.dispatchFeedback = 'Documento público publicado com sucesso para todo o ambiente.';
        setTimeout(() => this.closeDispatchModal(), 1800);
      },
      error: (err) => {
        this.savingDispatch = false;
        this.dispatchFeedback = err?.error?.error || 'Não foi possível publicar o documento público.';
      }
    });
  }

  private publishExistingDocumentAsPublic(documentId: number) {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id) {
      this.dispatchFeedback = 'Usuário inválido.';
      return;
    }
    this.savingDispatch = true;
    this.documentService.publishPublicDocument(documentId, {
      tenant_id: this.currentUser.tenant_id,
      user_id: this.currentUser.id
    }).subscribe({
      next: () => {
        this.savingDispatch = false;
        this.dispatchFeedback = 'Documento público publicado com sucesso para todo o ambiente.';
        setTimeout(() => this.closeDispatchModal(), 1800);
      },
      error: (err) => {
        this.savingDispatch = false;
        this.dispatchFeedback = err?.error?.error || 'Não foi possível publicar o documento público.';
      }
    });
  }

  private executeDispatch(documentId: number) {
    if (!this.currentUser) {
      return;
    }
    const normalizedId = Number(documentId);
    if (!normalizedId) {
      this.dispatchFeedback = 'Documento inválido.';
      this.savingDispatch = false;
      return;
    }
    const payload: any = {
      tenant_id: this.currentUser.tenant_id,
      document_id: normalizedId,
      sent_type: this.dispatchAudience,
      sent_by: this.currentUser.id
    };

    if (this.dispatchAudience === 'SPECIFIC_USERS') {
      const selected = Array.from(this.selectedUserIds);
      if (!selected.length) {
        this.savingDispatch = false;
        this.dispatchFeedback = 'Selecione ao menos um usuário.';
        return;
      }
      payload.user_ids = selected;
    }

    this.savingDispatch = true;
    this.documentService.dispatchDocument(payload).subscribe({
      next: (result) => {
        this.savingDispatch = false;
        this.dispatchFeedback = `Documento enviado para ${result.recipients} usuário(s).`;
        setTimeout(() => this.closeDispatchModal(), 1800);
      },
      error: (err) => {
        this.savingDispatch = false;
        this.dispatchFeedback = err?.error?.error || 'Não foi possível enviar o documento.';
      }
    });
  }

  private loadDocuments() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.loadingDocuments = true;
    this.documentService.listDocuments(this.currentUser.tenant_id).subscribe({
      next: (docs) => {
        this.existingDocuments = docs;
        this.loadingDocuments = false;
        if (!this.selectedDocumentId && docs.length) {
          this.selectedDocumentId = docs[0].id;
        }
      },
      error: () => {
        this.loadingDocuments = false;
      }
    });
  }

  private loadTenantUsers() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.loadingRecipients = true;
    this.userService.getUsersByTenant(this.currentUser.tenant_id).subscribe({
      next: (users) => {
        this.tenantUsers = users;
        this.loadingRecipients = false;
      },
      error: () => {
        this.loadingRecipients = false;
      }
    });
  }

  private resetDispatchState() {
    this.documentSource = 'existing';
    this.selectedDocumentId = null;
    this.newDocumentForm = {
      title: '',
      description: '',
      document_type: 'POLICY',
      is_public_document: false,
      require_read: true,
      require_accept: false,
      require_agreement: false,
      require_signature: false,
      require_selfie: false,
      require_geolocation: false
    };
    this.documentFile = null;
    this.documentFileName = '';
    this.documentFileError = '';
    this.dispatchAudience = 'ALL_USERS';
    this.selectedUserIds.clear();
    this.documentSearch = '';
    this.userSearch = '';
    this.savingDispatch = false;
    this.dispatchFeedback = '';
  }

  openQuestionDispatchModal() {
    if (!this.currentUser) {
      return;
    }
    this.resetQuestionDispatchState();
    this.questionDispatchOpen = true;
    this.loadIntranetSurveys();
    this.loadQuestionSectors();
    this.loadTenantUsers();
  }

  closeQuestionDispatchModal() {
    this.questionDispatchOpen = false;
    this.questionDispatchFeedback = '';
  }

  get filteredIntranetSurveys() {
    if (!this.surveySearch) {
      return this.intranetSurveys;
    }
    const term = this.surveySearch.toLowerCase();
    return this.intranetSurveys.filter((survey) => survey.title?.toLowerCase().includes(term));
  }

  dispatchQuestionnaire() {
    if (!this.currentUser) {
      return;
    }
    this.questionDispatchFeedback = '';
    const senderId = Number(this.currentUser.id ?? this.currentUser.user_id);
    if (!senderId) {
      this.questionDispatchFeedback = 'Não foi possível identificar o remetente do disparo.';
      return;
    }
    const survey = this.intranetSurveys.find((item) => item.id === Number(this.selectedSurveyId));
    if (!survey) {
      this.questionDispatchFeedback = 'Selecione um questionário.';
      return;
    }
    const payload: any = {
      tenant_id: this.currentUser.tenant_id,
      survey_id: survey.id,
      sent_type: this.questionAudience,
      sent_by: senderId
    };
    if (this.questionAudience === 'SECTOR') {
      if (!this.selectedSectorId) {
        this.questionDispatchFeedback = 'Selecione um setor.';
        return;
      }
      payload.sector_id = this.selectedSectorId;
    } else if (this.questionAudience === 'DEPARTMENT') {
      if (!this.selectedDepartmentId) {
        this.questionDispatchFeedback = 'Selecione um departamento.';
        return;
      }
      payload.department_id = this.selectedDepartmentId;
    } else if (this.questionAudience === 'LOCATION') {
      if (!this.selectedLocationId) {
        this.questionDispatchFeedback = 'Selecione um local.';
        return;
      }
      payload.location_id = this.selectedLocationId;
    } else if (this.questionAudience === 'SPECIFIC_USERS') {
      const selectedUsers = Array.from(this.selectedQuestionUserIds);
      if (!selectedUsers.length) {
        this.questionDispatchFeedback = 'Selecione pelo menos um usuário.';
        return;
      }
      payload.user_ids = selectedUsers;
    }

    this.savingQuestionDispatch = true;
    this.controlquestion.dispatchSurvey(payload).subscribe({
      next: (result: any) => {
        this.savingQuestionDispatch = false;
        const recipients = Number(result?.recipients || 0);
        this.questionDispatchFeedback = recipients > 0
          ? `Questionário disparado para ${recipients} usuário(s).`
          : 'Questionário disparado na intranet.';
        this.loadQuestionDispatches(1);
      },
      error: (errorMessage) => {
        this.savingQuestionDispatch = false;
        const fallback = 'Não foi possível disparar o questionário.';
        if (typeof errorMessage === 'string') {
          this.questionDispatchFeedback = errorMessage;
          return;
        }
        const apiMessage = errorMessage?.error?.error || errorMessage?.error?.detail || errorMessage?.message;
        this.questionDispatchFeedback = apiMessage || fallback;
      }
    });
  }

  private loadIntranetSurveys() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.loadingSurveys = true;
    this.controlquestion.listSurveys(this.currentUser.tenant_id, { intranet_only: 1 }).subscribe({
      next: (surveys) => {
        this.intranetSurveys = surveys || [];
        this.loadingSurveys = false;
        if (!this.selectedSurveyId && this.intranetSurveys.length) {
          this.selectedSurveyId = this.intranetSurveys[0].id;
        }
      },
      error: () => {
        this.loadingSurveys = false;
      }
    });
  }

  private resetQuestionDispatchState() {
    this.surveySearch = '';
    this.selectedSurveyId = null;
    this.questionDispatchFeedback = '';
    this.savingQuestionDispatch = false;
    this.questionAudience = 'ALL_USERS';
    this.selectedSectorId = null;
    this.selectedDepartmentId = null;
    this.selectedLocationId = null;
    this.selectedQuestionUserIds.clear();
    this.questionUserSearch = '';
  }

  onQuestionAudienceChange(mode: 'ALL_USERS' | 'SECTOR' | 'DEPARTMENT' | 'LOCATION' | 'SPECIFIC_USERS') {
    this.questionAudience = mode;
    this.selectedQuestionUserIds.clear();
    if (mode === 'DEPARTMENT') {
      this.loadQuestionDepartments();
    }
    if (mode === 'LOCATION') {
      this.loadQuestionDepartments();
      this.loadQuestionLocations();
    }
  }

  toggleQuestionRecipientSelection(userId: number) {
    if (this.selectedQuestionUserIds.has(userId)) {
      this.selectedQuestionUserIds.delete(userId);
    } else {
      this.selectedQuestionUserIds.add(userId);
    }
  }

  get filteredQuestionUsers() {
    if (!this.questionUserSearch) {
      return this.tenantUsers;
    }
    const term = this.questionUserSearch.toLowerCase();
    return this.tenantUsers.filter(user =>
      user.name?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term)
    );
  }

  private loadQuestionSectors() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.orgStructure.getSectors(this.currentUser.tenant_id).subscribe({
      next: (rows) => {
        this.questionSectors = rows || [];
      },
      error: () => {}
    });
  }

  loadQuestionDepartments() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.selectedDepartmentId = null;
    this.questionDepartments = [];
    this.questionLocations = [];
    this.orgStructure.getDepartments(this.currentUser.tenant_id, this.selectedSectorId).subscribe({
      next: (rows) => {
        this.questionDepartments = rows || [];
      },
      error: () => {}
    });
  }

  loadQuestionLocations() {
    if (!this.currentUser?.tenant_id) {
      return;
    }
    this.selectedLocationId = null;
    this.questionLocations = [];
    this.orgStructure.getLocations(this.currentUser.tenant_id, this.selectedDepartmentId).subscribe({
      next: (rows) => {
        this.questionLocations = rows || [];
      },
      error: () => {}
    });
  }

  private loadQuestionDispatches(page = 1) {
    if (!this.currentUser?.tenant_id) {
      this.questionDispatches = [];
      this.questionDispatchesTotal = 0;
      return;
    }
    this.questionDispatchesLoading = true;
    this.questionDispatchesError = '';
    this.questionDispatchesPage = page;
    this.controlquestion.listDispatches(this.currentUser.tenant_id, page, this.questionDispatchesPageSize).subscribe({
      next: (response) => {
        this.questionDispatches = Array.isArray(response?.items) ? response.items : [];
        this.questionDispatchesTotal = Number(response?.total || 0);
        this.questionDispatchesLoading = false;
      },
      error: (err) => {
        this.questionDispatches = [];
        this.questionDispatchesTotal = 0;
        this.questionDispatchesLoading = false;
        this.questionDispatchesError = err?.error?.error || 'Não foi possível carregar os questionários enviados.';
      }
    });
  }

  changeQuestionDispatchPage(direction: number) {
    const next = this.questionDispatchesPage + direction;
    const total = this.questionDispatchesTotalPages;
    if (next < 1 || next > total) {
      return;
    }
    this.loadQuestionDispatches(next);
  }

  get questionDispatchesTotalPages() {
    return Math.max(1, Math.ceil(this.questionDispatchesTotal / this.questionDispatchesPageSize));
  }
}
